import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraDivider,
  ChakraFlex,
  ChakraVStack,
  FAIconName,
  FireflyButton,
  SkeletonLoader,
  Text,
} from '@equip.health/ui';

import { AssessmentEvents } from '~/analytics';
import {
  AssessmentEventAttribute,
  AssessmentEventSource,
} from '~/analytics/assessmentEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CreateAssessmentDiagnosisOverrideModal } from '~/components/maud/patients/profile/assessments-redesign/CreateAssessmentDiagnosisOverrideModal';
import { DeleteAssessmentConfirmModal } from '~/components/maud/patients/profile/assessments-redesign/DeleteAssessmentConfirmModal';
import { GroupedAssessmentCard } from '~/components/maud/patients/profile/assessments-redesign/GroupedAssessmentCard';
import { AssessmentAction } from '~/lib/constants/patients.constants';
import { AssessmentChartDataScoreStrategyMap } from '~/lib/constants/survey/AssessmentChartDataScoreStrategyMap';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import {
  ASSESSMENT_CARD_TOP_LEVEL_ID,
  useAssessmentListScrollObserver,
} from '~/lib/hooks/chat/useAssessmentListScrollObserver';
import { CategorizedAssessments } from '~/lib/types/survey/CategorizedAssessments';
import { sortCategorizedAssessmentsByAssessmentLength } from '~/lib/util/assessmentIndexList.util';
import {
  AssessmentTimepointContext,
  filterGroupedAssessmentToCurrentEpisode,
} from '~/lib/util/assessmentTimepoint.util';
import {
  ClinicianAssessmentsSurveyCards,
  isSurveyRestrictedByDiagnosis,
} from '~/lib/util/survey.util';
import {
  onRecordNewSurvey,
  onSendNewSurvey,
} from '~/lib/util/surveyActions.util';
import { useGetPatientDiagnosesQuery } from '~/store/services/diagnoses';

type AssessmentsCardListProps = {
  patientId: string;
  assessmentTimepointContext?: AssessmentTimepointContext;
  categorizedAssessments: CategorizedAssessments;
  isAssessmentTabUiRevampEnabled: boolean;
  isGetGroupedAssessmentsLoading: boolean;
  setSelectedSurveyId: (surveyId: string | null) => void;
  mostRecentClickSurveyId: string | null;
  patientPreferredFullName: string;
  // @deprecated legacy view assessment modal
  setViewAssessmentModalData: Dispatch<SetStateAction<SurveyDetail | null>>;
  updateAssessmentState: Dispatch<AssessmentAction>;
};

export const ASSESSMENT_CARD_LIST_TOP_LEVEL_ID = 'assessment-card-list';
const CORE_CLINICAL_ASSESSMENTS_SUBCATEGORY_NAME = 'Core Clinical Assessments';

const AssessmentsCardList = ({
  assessmentTimepointContext,
  categorizedAssessments,
  isAssessmentTabUiRevampEnabled,
  isGetGroupedAssessmentsLoading,
  setSelectedSurveyId,
  mostRecentClickSurveyId,
  patientId,
  patientPreferredFullName,
  setViewAssessmentModalData,
  updateAssessmentState,
}: AssessmentsCardListProps) => {
  const { trackEvent } = useAnalytics();
  const [
    deleteAssessmentModalAssessmentId,
    setDeleteAssessmentModalAssessmentId,
  ] = useState('');

  const {
    data: patientDiagnosesData,
    isLoading: isGetPatientDiagnosesLoading,
  } = useGetPatientDiagnosesQuery(patientId);

  const [
    diagnosisOverrideModalSurveyName,
    setDiagnosisOverrideModalSurveyName,
  ] = useState<SurveyName | null>(null);

  useAssessmentListScrollObserver(
    categorizedAssessments,
    mostRecentClickSurveyId,
    setSelectedSurveyId,
  );

  const categorizedAssessmentsByAssessmentLength: CategorizedAssessments =
    useMemo(() => {
      if (Object.keys(categorizedAssessments).length > 0) {
        return sortCategorizedAssessmentsByAssessmentLength(
          categorizedAssessments,
        );
      }
      return {};
    }, [categorizedAssessments]);

  const onCreateAssessment = (
    surveyName: SurveyName,
    jsonParameters: string,
  ) => {
    if (
      patientDiagnosesData &&
      isSurveyRestrictedByDiagnosis(jsonParameters, patientDiagnosesData)
    ) {
      setDiagnosisOverrideModalSurveyName(surveyName);
    } else {
      if (ClinicianAssessmentsSurveyCards.includes(surveyName)) {
        onRecordNewSurvey(surveyName, updateAssessmentState);
      } else onSendNewSurvey(surveyName, updateAssessmentState);
    }
  };

  return (
    <ChakraFlex
      flexDirection="column"
      id={`${ASSESSMENT_CARD_LIST_TOP_LEVEL_ID}`}
      overflow="auto"
      padding="24px 24px 40px 24px"
      width="100%"
    >
      {(isGetGroupedAssessmentsLoading || isGetPatientDiagnosesLoading) && (
        <SkeletonLoader
          height="400px"
          id={ASSESSMENT_CARD_TOP_LEVEL_ID}
          isLoaded={
            !isGetGroupedAssessmentsLoading && !isGetPatientDiagnosesLoading
          }
        />
      )}
      {!isGetGroupedAssessmentsLoading && !isGetPatientDiagnosesLoading && (
        <ChakraFlex alignItems="start" flexDirection="column" width="100%">
          {Object.entries(categorizedAssessmentsByAssessmentLength).map(
            (
              [categoryName, subCategories],
              categorizedAssessmentIndex,
              categorizedAssessmentsByAssessmentLength,
            ) => (
              <ChakraFlex
                alignItems="start"
                flexDirection="column"
                id={`${ASSESSMENT_CARD_LIST_TOP_LEVEL_ID}-category-container-${categoryName.replaceAll(' ', '_')}`}
                key={categoryName}
                width="100%"
              >
                <Text
                  color="neutral.primary"
                  data-testid={`${ASSESSMENT_CARD_LIST_TOP_LEVEL_ID}-category-header-${categoryName.replaceAll(
                    ' ',
                    '_',
                  )}`}
                  id={categoryName}
                  variant="h4"
                  width="100%"
                >
                  {categoryName}
                </Text>
                <ChakraVStack
                  alignItems="start"
                  gap={isAssessmentTabUiRevampEnabled ? '64px' : '32px'}
                  marginTop="32px"
                  width="100%"
                >
                  {Object.entries(subCategories).map(
                    ([subCategoryName, subCategoryData]) => {
                      const isCoreClinicalAssessmentsSubCategory =
                        isAssessmentTabUiRevampEnabled &&
                        subCategoryName ===
                          CORE_CLINICAL_ASSESSMENTS_SUBCATEGORY_NAME;
                      const subCategoryContentPaddingLeft =
                        isAssessmentTabUiRevampEnabled &&
                        !isCoreClinicalAssessmentsSubCategory
                          ? '24px'
                          : '0';

                      return (
                        <ChakraFlex
                          background={
                            isCoreClinicalAssessmentsSubCategory
                              ? 'secondary.5'
                              : 'transparent'
                          }
                          borderRadius={
                            isCoreClinicalAssessmentsSubCategory ? '12px' : '0'
                          }
                          flexDirection="column"
                          id={`${ASSESSMENT_CARD_LIST_TOP_LEVEL_ID}-subcategory-container-${subCategoryName.replaceAll(' ', '_')}`}
                          key={subCategoryName}
                          padding={
                            isCoreClinicalAssessmentsSubCategory ? '24px' : '0'
                          }
                          width="100%"
                        >
                          <Text
                            color={
                              isCoreClinicalAssessmentsSubCategory
                                ? 'secondary.70'
                                : 'neutral.secondary'
                            }
                            data-testid={`${ASSESSMENT_CARD_LIST_TOP_LEVEL_ID}-subcategory-header-${subCategoryName.replaceAll(
                              ' ',
                              '_',
                            )}`}
                            paddingLeft={subCategoryContentPaddingLeft}
                            variant="h5"
                            width="100%"
                          >
                            {subCategoryName}
                          </Text>

                          <ChakraFlex
                            alignItems="start"
                            flexDirection="row"
                            flexWrap="wrap"
                            gap="18px"
                            id={`${ASSESSMENT_CARD_LIST_TOP_LEVEL_ID}-subcategory-assessments-wrapper-${subCategoryName.replaceAll(' ', '_')}`}
                            marginTop="18px"
                            paddingLeft={subCategoryContentPaddingLeft}
                            width="100%"
                          >
                            {subCategoryData.assessments.map(
                              (groupedAssessment) => {
                                const isChartAssessment =
                                  !!AssessmentChartDataScoreStrategyMap[
                                    groupedAssessment.name
                                  ];
                                const currentEpisodeGroupedAssessment =
                                  isAssessmentTabUiRevampEnabled &&
                                  isChartAssessment
                                    ? filterGroupedAssessmentToCurrentEpisode(
                                        groupedAssessment,
                                        assessmentTimepointContext?.currentEpisode,
                                      )
                                    : groupedAssessment;
                                const shouldRenderAssessmentCard =
                                  currentEpisodeGroupedAssessment.assessments
                                    .length > 0;

                                return shouldRenderAssessmentCard ? (
                                  <GroupedAssessmentCard
                                    assessmentTimepointContext={
                                      assessmentTimepointContext
                                    }
                                    groupedAssessment={groupedAssessment}
                                    key={`grouped-assessment-card-${groupedAssessment.externalId}`}
                                    onCreateAssessment={onCreateAssessment}
                                    patientPreferredFullName={
                                      patientPreferredFullName
                                    }
                                    setDeleteAssessmentModalAssessmentId={
                                      setDeleteAssessmentModalAssessmentId
                                    }
                                    setViewAssessmentModalData={
                                      setViewAssessmentModalData
                                    }
                                  />
                                ) : (
                                  <ChakraBox
                                    id={`assessment-card__${groupedAssessment.externalId}`}
                                    key={`assessment-card-empty-btn__${groupedAssessment.externalId}`}
                                  >
                                    <FireflyButton
                                      id={groupedAssessment.externalId}
                                      leftIcon={FAIconName.plusSolid}
                                      onClick={() => {
                                        trackEvent(
                                          AssessmentEvents.createNewAssessmentClicked,
                                          {
                                            [AssessmentEventAttribute.SurveyName]:
                                              groupedAssessment.name,
                                            [AssessmentEventAttribute.Source]:
                                              AssessmentEventSource.AssessmentEmptyStateCard,
                                          },
                                        );
                                        onCreateAssessment(
                                          groupedAssessment.name,
                                          groupedAssessment.jsonParameters,
                                        );
                                      }}
                                      variant="secondary"
                                    >
                                      {groupedAssessment.displayName}
                                    </FireflyButton>
                                  </ChakraBox>
                                );
                              },
                            )}
                          </ChakraFlex>
                        </ChakraFlex>
                      );
                    },
                  )}
                </ChakraVStack>
                {(isAssessmentTabUiRevampEnabled
                  ? categorizedAssessmentIndex <
                    categorizedAssessmentsByAssessmentLength.length - 1
                  : categorizedAssessmentsByAssessmentLength.length >
                    categorizedAssessmentIndex) && (
                  <ChakraDivider
                    colorScheme="line"
                    id={`${categoryName}__divider`}
                    marginY="64px"
                  />
                )}
              </ChakraFlex>
            ),
          )}
          <CreateAssessmentDiagnosisOverrideModal
            closeModal={() => setDiagnosisOverrideModalSurveyName(null)}
            isOpen={diagnosisOverrideModalSurveyName !== null}
            onConfirmClick={() => {
              onRecordNewSurvey(
                diagnosisOverrideModalSurveyName,
                updateAssessmentState,
              );
              setDiagnosisOverrideModalSurveyName(null);
            }}
            patientPreferredFullName={patientPreferredFullName}
          />
          <DeleteAssessmentConfirmModal
            assessmentId={deleteAssessmentModalAssessmentId}
            setAssessmentId={setDeleteAssessmentModalAssessmentId}
          />
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};

export default AssessmentsCardList;
